// gsaCalc.js — pure wrapper around labEngine.gsa(): adds input sanity checks and the derived values the report needs.
import { gsa, checkResolution } from '../labEngine';

const CLAUSE = 'IS 2720-4:1985';
export const COARSE_SIZES = [100, 75, 19, 4.75];
export const FINE_SIZES = [2, 0.425, 0.075];
export const COARSE_LABELS = ['100 mm', '75 mm', '19 mm', '4.75 mm'];
export const FINE_LABELS = ['2.00 mm', '425 µ', '75 µ'];

const sum = (xs) => xs.reduce((a, b) => a + b, 0);
const blockIssue = (code, clause, msg) => ({ level: 'block', code, clause, msg });

// totalDryG, wetG (optional / null), coarseRetainedG[4], partial { massG, retainedG[3], method, panG }, hasClay
export function computeGSA({ totalDryG, wetG = null, coarseRetainedG, partial, hasClay = false }) {
  const issues = [];

  // 1) raw readings must be believable balance readings (catches back-calculated values like 1105.751)
  const readings = [
    ['Total dry sample', totalDryG],
    ...(wetG != null ? [['Wet soil weight', wetG]] : []),
    ...coarseRetainedG.map((v, i) => [`Retained on ${COARSE_LABELS[i]}`, v]),
    ['Sub-sample weight', partial.massG],
    ...partial.retainedG.map((v, i) => [`Retained on ${FINE_LABELS[i]}`, v]),
    ...(partial.method === 'dry' && Number.isFinite(partial.panG) ? [['Pan weight', partial.panG]] : []), // a missing pan is reported by the engine
  ];
  readings.forEach(([label, v]) => issues.push(checkResolution(label, v, 0.01, `${CLAUSE} 3.1`)));

  // 2) weights must be physically consistent
  const coarseSum = sum(coarseRetainedG);
  const avail = totalDryG - coarseSum; // dry soil passing 4.75 mm
  let structural = false;
  const bad = (code, clause, msg) => { structural = true; issues.push(blockIssue(code, clause, msg)); };
  if (!(totalDryG > 0)) bad('TOTAL', CLAUSE, 'Total dry sample weight must be above 0');
  else if (coarseSum >= totalDryG)
    bad('COARSE_SUM', CLAUSE, `Retained on the coarse sieves (${coarseSum.toFixed(2)} g) must be less than the total sample (${totalDryG} g)`);
  if (!(partial.massG > 0)) bad('PARTIAL', CLAUSE, 'Sub-sample weight must be above 0');
  else if (avail > 0 && partial.massG > avail + 1e-6)
    bad('PARTIAL_MASS', CLAUSE, `Sub-sample (${partial.massG} g) is more than the soil passing 4.75 mm (${avail.toFixed(2)} g)`);
  if (sum(partial.retainedG) > partial.massG + 1e-6)
    bad('FINE_SUM', CLAUSE, `Retained on the fine sieves (${sum(partial.retainedG).toFixed(2)} g) is more than the sub-sample (${partial.massG} g)`);
  if (wetG != null && wetG < totalDryG)
    bad('WET_LT_DRY', 'IS 2720-2:1973', 'Wet soil weight cannot be less than the dry weight');
  if (structural) {
    if (partial.method === 'dry' && !Number.isFinite(partial.panG))
      issues.push(blockIssue('NO_PAN', CLAUSE, 'Pan weight required for dry sieving (mass balance check)'));
    return { result: null, issues: issues.filter(Boolean) };
  }

  // 3) engine
  const out = gsa({ totalDryG, coarseRetainedG, partial, hasClay });
  const p = (s) => out.result.rows.find((r) => r.size === s).pctPassing;
  const result = {
    ...out.result,
    cobblePct: 100 - p(75),                 // retained on 75 mm
    gravelPct: p(75) - p(4.75),             // 75 mm → 4.75 mm (cobble shown separately, so it is not counted twice)
    availG: avail,
    scaledFineG: partial.retainedG.map((w) => (w * avail) / partial.massG), // fine-sieve weights scaled to the whole sample
    nmcPct: wetG != null ? ((wetG - totalDryG) / totalDryG) * 100 : null,
  };
  return { result, issues: [...issues, ...out.issues].filter(Boolean) };
}